/*
 * Hover video for collection grid cards (fm_field.product_card_hover_media).
 * Mirrors the Dynamic Product Slider card media in collection-slider-tabs.js:
 * - Desktop (990px+): plays on mouse enter / focus, resets on leave.
 * - Below 990px: plays once when the card is 50% in view, for
 *   data-mobile-duration seconds or until the video ends. Drops below 20%
 *   visible to re-arm, so it replays on re-entry.
 * A custom element, so cards re-rendered by facet AJAX bind themselves again.
 * State classes go on the .card-wrapper; component-card.css reads them.
 */
if (!customElements.get('card-hover-media')) {
  const DESKTOP_QUERY = '(min-width: 990px)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  customElements.define(
    'card-hover-media',
    class CardHoverMedia extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        this.card = this.closest('.card-wrapper');
        if (!this.video || !this.card) return;

        this.durationMs = (parseInt(this.dataset.mobileDuration, 10) || 5) * 1000;
        this.timerId = null;

        this.onEnter = this.onEnter.bind(this);
        this.onLeave = this.onLeave.bind(this);
        this.endMobileVideo = this.endMobileVideo.bind(this);

        this.card.addEventListener('mouseenter', this.onEnter);
        this.card.addEventListener('mouseleave', this.onLeave);
        this.card.addEventListener('focusin', this.onEnter);
        this.card.addEventListener('focusout', this.onLeave);

        if (typeof IntersectionObserver === 'undefined') return;

        /* Same thresholds as bindMobileCardMedia: the gap between 20% and
           50% prevents reset/restart jitter at one boundary. */
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (this.isDesktop()) return;
              if (!entry.isIntersecting || entry.intersectionRatio < 0.2) {
                this.resetMobileVideo();
                return;
              }
              if (entry.intersectionRatio < 0.5) return;
              this.startMobileVideo();
            });
          },
          { root: null, threshold: [0, 0.2, 0.5] }
        );
        this.observer.observe(this.card);
      }

      disconnectedCallback() {
        if (!this.card) return;
        this.card.removeEventListener('mouseenter', this.onEnter);
        this.card.removeEventListener('mouseleave', this.onLeave);
        this.card.removeEventListener('focusin', this.onEnter);
        this.card.removeEventListener('focusout', this.onLeave);
        if (this.observer) this.observer.disconnect();
        this.clearTimer();
      }

      isDesktop() {
        return window.matchMedia(DESKTOP_QUERY).matches;
      }

      rewind() {
        this.video.pause();
        try {
          this.video.currentTime = 0;
        } catch (e) {
          /* noop */
        }
      }

      clearTimer() {
        if (this.timerId) {
          clearTimeout(this.timerId);
          this.timerId = null;
        }
      }

      onEnter() {
        if (!this.isDesktop()) return;
        this.card.classList.add('is-media-hover');
        this.video.play().catch(() => {});
      }

      onLeave() {
        if (!this.isDesktop()) return;
        this.card.classList.remove('is-media-hover');
        this.rewind();
      }

      startMobileVideo() {
        if (window.matchMedia(REDUCED_MOTION_QUERY).matches) return;
        if (this.card.classList.contains('is-mobile-video-ended')) return;
        if (this.card.classList.contains('is-mobile-video-playing')) return;

        this.card.classList.add('is-mobile-video-playing');
        this.video.loop = false;
        this.video.addEventListener('ended', this.endMobileVideo, { once: true });
        try {
          this.video.currentTime = 0;
        } catch (e) {
          /* noop */
        }
        this.video.play().catch(() => {});

        this.clearTimer();
        this.timerId = setTimeout(this.endMobileVideo, this.durationMs);
      }

      endMobileVideo() {
        this.clearTimer();
        this.video.removeEventListener('ended', this.endMobileVideo);
        this.card.classList.remove('is-mobile-video-playing');
        this.card.classList.add('is-mobile-video-ended');
        this.video.loop = true;
        this.rewind();
      }

      resetMobileVideo() {
        this.clearTimer();
        this.video.removeEventListener('ended', this.endMobileVideo);
        this.card.classList.remove('is-mobile-video-playing', 'is-mobile-video-ended');
        this.video.loop = true;
        this.rewind();
      }
    }
  );
}
